import React, { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmationBarDialog } from "../dialog/confirmation-bar/confirmation-bar-dialog";
import { MapContainer, TileLayer } from "react-leaflet";
import { LatLngTuple, Map } from "leaflet";
import "leaflet/dist/leaflet.css";

import "./bottom-photo-maps-dialog.css";
import { useAppContext } from "../../hooks/useAppContext";
import { useMapStyles } from "./hooks/use-map-styles";
import { PlaceholderImage } from "../placeholder";
import { ActiveBox } from "../active-box";

import { labels } from "./labels";
import { useLabels } from "../../hooks/useLabels";
import { BusyBlocker } from "../busy-blocker/busy-blocker";
import { useErrorFeedback } from "../../hooks/useErrorFeedback";
import { SearchBox, useSearchBoxCore } from "@mapbox/search-js-react";
import { useQuery } from "@tanstack/react-query";
import { usePinStyles } from "./hooks/use-pin-styles";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";

/** These values can be found in osm-tile-server/config.json */
const maxSize = 8192;
const maxScale = 9;

// Remember a session token for the search box
let sessionToken = "";

const defaultMapPhotoSourceData: Required<eXpress.client.IMapPhotoSourceData> = {
	location: "Brussels",
	mapStyle: "basic",
	pinStyle: undefined,
	lng: 4.3185893,
	lat: 50.8493131,
	zoom: 12,
	url: "",
	height: 0,
	width: 0,
};

type SearchBoxFeatureSuggestion = Parameters<
	NonNullable<Parameters<typeof SearchBox>[0]["onRetrieve"]>
>[0]["features"][number];

export const BottomPhotoMapsDialog = ({
	data,
	onOk,
	onCancel,
}: {
	data: Partial<eXpress.client.IMapPhotoSourceData> | undefined;
	onOk: ({ lat, lng, zoom }: eXpress.client.IMapPhotoSourceData) => void;
	onCancel: () => void;
}) => {
	// Only add default location and coordinates if no location is set
	const initialData: Partial<eXpress.client.IMapPhotoSourceData> = {
		mapStyle: defaultMapPhotoSourceData.mapStyle,
		...data,
	};

	if (initialData.location === undefined) {
		initialData.location = defaultMapPhotoSourceData.location;
		initialData.lat = defaultMapPhotoSourceData.lat;
		initialData.lng = defaultMapPhotoSourceData.lng;
		initialData.zoom = defaultMapPhotoSourceData.zoom;
	}

	const mapRef = useRef<Map>(null);
	const [selectedAddress, setSelectedAddress] = useState<string>(initialData.location ?? "");
	const {
		context,
		reactQueryNoReloadOptions,
		platform: { guidString },
	} = useAppContext();

	// Temporary for this issue
	const showPinStyle = !!useFeatureFlagToggle("addDefaultPinStyle");

	const center = useMemo(
		() => ({
			lat: initialData.lat ?? 0,
			lng: initialData.lng ?? 0,
		}),
		[initialData.lat, initialData.lng],
	);

	useEffect(() => {
		mapRef.current?.setView(center, initialData.zoom);
	}, [center, initialData.zoom]);

	const [[mapStyleTitle]] = useLabels([labels.mapStyleTitle]);

	const [selectedStyle, setSelectedStyle] = useState<string>(initialData.mapStyle ?? "basic");
	const {
		data: mapStyles = [],
		isLoading: isLoadingMapStyles,
		error: mapStylesError,
	} = useMapStyles();

	const {
		data: pinStyles = [],
		isLoading: isLoadingPinStyles,
		error: pinStylesError,
	} = usePinStyles();

	// Ensure a valid style if used
	// If style was removed fom server we fallback to the default style
	useEffect(() => {
		if (mapStyles.length > 0 && !mapStyles.some((style) => style.id === selectedStyle)) {
			setSelectedStyle(mapStyles[0].id);
		}
	}, [mapStyles, selectedStyle]);

	const keyDownUsedRef = useRef(false);
	const resultsListEl = document.querySelector("[class*=ResultsList]");

	// select first suggestion when pressing enter
	// this is a workaround for the fact that the search box does not support this
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Enter" && !keyDownUsedRef.current) {
				const firstElement = document.querySelector('[id*="-ResultsList-0"]');
				(firstElement as HTMLElement)?.click();
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	// Show error toast when load styles fails
	const { errorFeedback } = useErrorFeedback();
	useEffect(() => {
		if (mapStylesError || pinStylesError) {
			errorFeedback();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapStylesError || pinStylesError]);

	const ref = useRef<{
		focus(): void;
	} | null>(null);

	// On mount, we load the location
	useEffect(() => {
		ref.current?.focus();
	}, []);

	const searchBoxOptions: Partial<Parameters<typeof SearchBox>[0]["options"]> = {
		language: context.language, // IETF language tag
		types: "country, region, place, postcode, street, address, locality",
		// later we should set set this from the default location configured on a design/zone
		// this is used to sort the results on proximity
		// this way results will be sorted closest->furthest, based on the default map location and should give good results for different countries
		...(initialData.lng && initialData.lat
			? { proximity: `${initialData.lng},${initialData.lat}` }
			: {}),
	};

	function setMapLocation(feature: SearchBoxFeatureSuggestion) {
		setSelectedAddress(feature.properties.full_address);

		const bounds = feature.properties.bbox;
		if (bounds) {
			// if there are bounds we can use those to go to the correct location, zoom will automatically change to bounding box
			const northWest: LatLngTuple = [bounds[1], bounds[0]];
			const southEast: LatLngTuple = [bounds[3], bounds[2]];

			mapRef.current?.fitBounds([northWest, southEast], {
				animate: false,
			});
		} else {
			// if no bounds then go to exact location with zoomlevel (if no bounds are available it is usually a street or address)
			mapRef.current?.setView(
				[feature.geometry.coordinates[1], feature.geometry.coordinates[0]],
				17,
			);
		}
	}

	// https://docs.mapbox.com/mapbox-search-js/api/react/search/#usesearchboxcore
	const searchBoxCore = useSearchBoxCore({ accessToken: context.osmMapboxAccessToken });

	const { isFetching: isLoadingDefaultLocation } = useQuery<null>(
		["use-search-box", initialData.location],
		async () => {
			// Create a session per user, billing and rate limit is per session
			sessionToken ||= guidString.create();
			if (!initialData.location) return null;

			const { suggestions } = await searchBoxCore.suggest(initialData.location, {
				...searchBoxOptions,
				sessionToken,
				limit: 1,
				// No streets, POI or adresses
				types: "country, place",
			});

			const suggestion = suggestions?.[0];
			if (!suggestion) return null;

			const result = await searchBoxCore.retrieve(suggestion, {
				sessionToken,
				language: context.language, // IETF language tag
			});

			if (result.features.length > 0) {
				const feature = result.features[0];
				setMapLocation(feature);
			}

			return null;
		},
		{
			...reactQueryNoReloadOptions,
			enabled: !!context.osmMapboxAccessToken && !!initialData.location && !initialData.lat,

			// Because we don't return a value, we need to set the cache time to 0 to retrigger at mount
			cacheTime: 0,
			meta: {
				logToServer: true,
				errorPrefix: "Error loading initial location",
			},
		},
	);

	return (
		<ConfirmationBarDialog
			okDisabled={
				isLoadingMapStyles || !!mapStylesError || isLoadingPinStyles || !!pinStylesError
			}
			onCancel={onCancel}
			onOk={() => {
				const map = mapRef.current;
				if (!map) {
					onCancel();
					return;
				}
				const { lat, lng } = map.getCenter();
				const zoom = map.getZoom();

				const zoneSize = map.getSize();
				const scaleFactor = Math.min(
					maxScale,
					Math.floor(Math.max(maxSize / zoneSize.x, maxSize / zoneSize.y)),
				);

				// width/height is limited by 'maxSize' prop in osm-tile-server/config.json, currently set to 4096
				// to guarantee the same level of detail on the static image as in the interactive map, we need to get an image with the same zoomlevel
				// we can use the '@6x' prop at the end of the url, this will render every tile in the image x times bigger, so the resulting image is x times bigger
				// the max value we can use after @ is 9 (currently 6, defined in config.json), to be checked if this results in a high enough quality image
				onOk({
					...initialData,
					lat,
					lng,
					zoom,
					location: selectedAddress,
					mapStyle: selectedStyle,
					pinStyle: initialData.pinStyle, // TODO: pass pin style chosen by user
					url: `${context.osmTileServerUrl}/styles/${selectedStyle}/static/${
						map.getCenter().lng
					},${map.getCenter().lat},${map.getZoom()}/${zoneSize.x}x${
						zoneSize.y
					}@${scaleFactor}x.png`,
					width: zoneSize.x * scaleFactor,
					height: zoneSize.y * scaleFactor,
				});
			}}
		>
			<>
				<div className="bottom-photo-maps-dialog__dialog-content">
					<MapContainer
						attributionControl={false}
						className="bottom-photo-maps-dialog__map-container"
						center={center}
						zoom={initialData.zoom}
						scrollWheelZoom={true}
						ref={mapRef}
					>
						<TileLayer
							url={`${context.osmTileServerUrl}/styles/${selectedStyle}/{z}/{x}/{y}.png`}
						/>
					</MapContainer>
					<SearchBox
						ref={ref}
						accessToken={context.osmMapboxAccessToken}
						options={searchBoxOptions}
						onChange={() => {
							keyDownUsedRef.current = false;
							resultsListEl?.scroll({
								top: 0,
							});
						}}
						onRetrieve={(result) => {
							keyDownUsedRef.current = true;
							const feature = result.features[0];
							setMapLocation(feature);
						}}
						value={selectedAddress}
						theme={{
							cssText: `.Results 
							{
								position: fixed;
								display: flex;
								flex-direction: column;
								height: -webkit-fill-available;
								margin-bottom: 1rem;
							}
							.ResultsList
							{
								display: flex;
								flex-direction: column;
								overflow-y: scroll;
							}
							.ResultsAttribution {
								display: none;
							}
							.ClearBtn {
								display: ${selectedAddress.length ? "block" : "none"};
							}
	`,
						}}
					/>
					<div className="bottom-photo-maps-dialog__map-styles-container">
						<div>{mapStyleTitle}</div>
						<div className="bottom-photo-maps-dialog__map-styles-list">
							{mapStyles.map((mapStyle) => (
								<ActiveBox
									key={mapStyle.id}
									isActive={mapStyle.id === selectedStyle}
									className="bottom-photo-maps-dialog__map-style"
								>
									<PlaceholderImage
										className="bottom-photo-maps-dialog__map-style-image"
										src={mapStyle.url}
										onClick={() => {
											setSelectedStyle(mapStyle.id);
										}}
									/>
									<div className="bottom-photo-maps-dialog__map-style-name">
										{mapStyle.name}
									</div>
								</ActiveBox>
							))}
						</div>
					</div>

					{showPinStyle ? (
						<div>
							Temporary: no click behavior
							<br />
							{pinStyles?.map((pinStyle) => (
								<PlaceholderImage
									key={pinStyle.id}
									style={{ height: 50, margin: 5, padding: 5 }}
									src={pinStyle.url}
								/>
							))}
						</div>
					) : null}
				</div>
				<BusyBlocker
					isOpen={isLoadingMapStyles || isLoadingPinStyles || isLoadingDefaultLocation}
					delayInMs={1000}
				/>
			</>
		</ConfirmationBarDialog>
	);
};
